"use client";

import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { networkingApi } from "@/lib/api/services/networking.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, User, UserPlus, MessageSquare, Users } from "lucide-react";
import Link from "next/link";
import { toast } from "@/components/ui/toast";

interface Connection {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
  createdAt: string;
}

export default function NetworkPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["network-hub"],
    queryFn: async () => {
      const [connData, followData, followingData] = await Promise.all([
        networkingApi.getConnections({ page: 1, perPage: 10 }),
        networkingApi.getFollowers({ page: 1, perPage: 10 }),
        networkingApi.getFollowing({ page: 1, perPage: 10 }),
      ]);
      return {
        connections: ((connData as unknown as { data?: Connection[] }).data ?? []) as Connection[],
        followers: ((followData as unknown as { data?: Connection[] }).data ?? []) as Connection[],
        following: ((followingData as unknown as { data?: Connection[] }).data ?? []) as Connection[],
      };
    },
  });

  const connections = data?.connections ?? [];
  const followers = data?.followers ?? [];
  const following = data?.following ?? [];

  const followMutation = useMutation({
    mutationFn: (userId: string) => networkingApi.follow(userId),
    onSuccess: () => {
      toast({ type: "success", title: "Now following", message: "You are now following this user." });
      queryClient.invalidateQueries({ queryKey: ["network-hub"] });
    },
    onError: () => toast({ type: "error", title: "Error", message: "Failed to follow user." }),
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="space-y-1">
          <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">Network</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">Manage your professional connections.</p>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link href="/"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Link>
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-6 text-center">
            <User className="mx-auto h-6 w-6 text-muted-foreground/50" />
            <p className="mt-2 text-xl font-semibold text-foreground tabular-nums">{connections.length}</p>
            <p className="text-xs text-muted-foreground tracking-wide uppercase">Connections</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <User className="mx-auto h-6 w-6 text-muted-foreground/50" />
            <p className="mt-2 text-xl font-semibold text-foreground tabular-nums">{followers.length}</p>
            <p className="text-xs text-muted-foreground tracking-wide uppercase">Followers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <User className="mx-auto h-6 w-6 text-muted-foreground/50" />
            <p className="mt-2 text-xl font-semibold text-foreground tabular-nums">{following.length}</p>
            <p className="text-xs text-muted-foreground tracking-wide uppercase">Following</p>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <Card className="mt-6">
          <CardContent className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-muted/10 animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 bg-muted/10 rounded animate-pulse" />
                  <div className="h-3 w-1/4 bg-muted/10 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="mt-6">
            <CardContent className="p-0">
              {connections.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">No connections yet.</div>
              ) : (
                <div className="divide-y divide-border/60">
                  {connections.map((conn) => (
                    <div key={conn.id} className="flex items-center justify-between p-4 transition-colors hover:bg-muted/10">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted/10 text-muted-foreground">
                          <User className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{conn.firstName} {conn.lastName}</p>
                          <p className="text-xs text-muted-foreground truncate">{conn.email}</p>
                        </div>
                      </div>
                      <span className="h-5 px-2 text-[0.6875rem] font-medium rounded-full border bg-muted/10 text-muted-foreground border-border capitalize shrink-0">{conn.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <Card>
              <CardContent className="p-0">
                {followers.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">No followers yet.</div>
                ) : (
                  <div className="divide-y divide-border/60">
                    {followers.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-4 transition-colors hover:bg-muted/10">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted/10 text-muted-foreground">
                            <User className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{user.firstName} {user.lastName}</p>
                            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="h-7 px-2.5 text-xs shrink-0" onClick={() => followMutation.mutate(user.id)} disabled={followMutation.isPending}>
                          <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Follow
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                {following.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">Not following anyone yet.</div>
                ) : (
                  <div className="divide-y divide-border/60">
                    {following.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-4 transition-colors hover:bg-muted/10">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted/10 text-muted-foreground">
                            <User className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{user.firstName} {user.lastName}</p>
                            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          </div>
                        </div>
                        <span className="h-5 px-2 text-[0.6875rem] font-medium rounded-full border bg-muted/10 text-muted-foreground border-border shrink-0">Following</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      <div className="mt-12 space-y-4">
        <h2 className="font-heading text-base font-semibold tracking-tight text-foreground">Next Steps</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <Link href="/messages" className="block">
            <Card className="transition-colors hover:border-border/80">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted/10 text-foreground"><MessageSquare className="h-5 w-5" /></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Messages</p>
                  <p className="text-xs text-muted-foreground">Connect with your network directly</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link href="/network/suggestions" className="block">
            <Card className="transition-colors hover:border-border/80">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted/10 text-foreground"><Users className="h-5 w-5" /></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Discover</p>
                  <p className="text-xs text-muted-foreground">Find new connections and communities</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
}
